import * as React from 'react';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useLoadDashboard} from 'src/components/analytics-genesis/byod/api/queries/useLoadDashboard';
import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {hydrateDashboardFromApi} from 'src/components/analytics-genesis/byod/store/hydrateDashboard';
import {ByodErrorBoundary} from 'src/components/analytics-genesis/byod/components/byod-error-boundary';
import {Canvas} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/canvas';

import css from './home-byod-viewer.css';


type HomeBYODViewerProps = {
  dashboardId: string;
  onError: () => void;
};

export function HomeBYODViewer({dashboardId, onError}: HomeBYODViewerProps) {
  const {t} = useLabelTranslation();
  const {data, isLoading, isError} = useLoadDashboard(dashboardId);
  const initDashboard = useDashboardStore((s) => s.initDashboard);
  const reset = useDashboardStore((s) => s.reset);
  const storeId = useDashboardStore((s) => s.id);
  const storeRowCount = useDashboardStore((s) => s.rowOrder.length);
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    if (data && data.id === dashboardId) {
      const result = hydrateDashboardFromApi(data);
      initDashboard(result.params);
      setHydrated(true);
    }
  }, [data, dashboardId, initDashboard]);

  // Reset hydrated flag when dashboardId changes
  React.useEffect(() => {
    setHydrated(false);
  }, [dashboardId]);

  React.useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  React.useEffect(() => {
    if (isError) {
      onError();
    }
  }, [isError, onError]);

  if (isLoading || (!hydrated && !isError)) {
    return (
      <div className={css.loadingContainer}>
        <CircularLoader size="large" />
      </div>
    );
  }

  if (isError || !data) {
    return null;
  }

  // Only render Canvas once the store has been fully hydrated with this
  // dashboard's data. Canvas has a hook after an early return on empty
  // rowOrder, so we must never mount it before rows are populated.
  if (storeId !== dashboardId) {
    return (
      <div className={css.loadingContainer}>
        <CircularLoader size="large" />
      </div>
    );
  }

  // Dashboard hydrated but has no rows (e.g. all widgets deleted via API).
  if (storeRowCount === 0) {
    return (
      <div className={css.loadingContainer}>
        <EmptyState
          title={t('Dashboard_is_empty', 'Dashboard is empty')}
          description={t(
            'This_dashboard_has_no_widgets',
            'This dashboard has no widgets.',
          )}
          imageVariant="data"
        />
      </div>
    );
  }

  return (
    <div className={css.container}>
      <ByodErrorBoundary variant="page" resetKey={dashboardId}>
        <Canvas pathname={`/byod/dashboard/${dashboardId}`} canEdit={false} />
      </ByodErrorBoundary>
    </div>
  );
}
